import React, { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { observer } from "mobx-react"
import { Button, Flex, Form, FormInstance } from "antd"
import { DefaultOptionType } from "antd/es/select"
import { PlusOutlined } from "@ant-design/icons"

import { paths } from "@src/app/paths"
import { MassPayoutsCollection } from "@src/pages/massPayouts/crypto/classess/MassPayoutsCryptoCollection"
import {
  DEFAULT_MANUAL_PAYOUTS_FORM,
  DEFAULT_PAYOUT_FORM,
} from "@src/pages/massPayouts/crypto/ui/manual/constants"
import { ManualPayoutsFormFee } from "@src/pages/massPayouts/crypto/ui/manual/ManualPayoutsFormFee"
import { ManualPayoutsFormItem } from "@src/pages/massPayouts/crypto/ui/manual/ManualPayoutsFormItem"
import { GlobalPayoutsConformationProcess } from "@src/pages/payoutsConfirmation/classes/PayoutsConformationProcess"
import { MassPayoutJsonCreateFeePayerEnum } from "@src/shared/api/generated"
import { SubmitButton } from "@src/shared/componemts/SubmitButton"
import { useCatchFormErrors } from "@src/shared/form/base_error"

type CryptoMassPayoutFormData = {
  address: string
  amount: string
  currency: string
}

export type ManualPayoutsFormType = {
  fee_payer: MassPayoutJsonCreateFeePayerEnum
  payouts: CryptoMassPayoutFormData[]
}
export type ManualPayoutsFormProps = {
  balances: any[]
  collection: MassPayoutsCollection
  currencies: {
    isLoading: boolean
    options: DefaultOptionType[]
  }
  form: FormInstance<ManualPayoutsFormType>
}
export const ManualPayoutsForm = observer(
  ({ balances, collection, currencies, form }: ManualPayoutsFormProps) => {
    const payouts = Form.useWatch("payouts", { form: form })

    const [formErrors, setErrors] = useState<
      ReturnType<FormInstance["getFieldsError"]>
    >([])

    const navigate = useNavigate()

    const p = useMemo(() => {
      if (!payouts) {
        return []
      }

      return payouts
        .filter(({ currency }) => Boolean(currency))
        .map((p) => {
          const [symbol, network] = p.currency.split(",")

          const { payouts_available = null } =
            balances.find(
              (balance) =>
                balance.symbol === symbol && balance.network === network,
            ) ?? {}

          return payouts_available?.mass
            ? Number(payouts_available.mass)
            : undefined
        })
    }, [payouts, balances])

    const { submit } = useCatchFormErrors(
      form,
      async (values: ManualPayoutsFormType) => {
        return collection.createMassPayoutJson(values).then((isCreated) => {
          if (isCreated) {
            navigate(paths.payoutsConfirmation)
            GlobalPayoutsConformationProcess().setState({
              id: isCreated.id,
              payouts: isCreated.payouts,
              type: "crypto",
            })

            form.resetFields()
          }
        })
      },
    )

    const formAdd = () => {
      form.setFieldValue("payouts", [
        ...form.getFieldValue("payouts"),
        { ...DEFAULT_PAYOUT_FORM },
      ])
    }

    return (
      <Form
        form={form}
        id="GENERATED_ID_MANUALPAYOUTSFORM_FORM_VOID_1"
        initialValues={DEFAULT_MANUAL_PAYOUTS_FORM}
        layout="vertical"
        name="manualPayouts"
        onFieldsChange={() => setErrors(form.getFieldsError())}
        onFinish={submit}
        size="large"
      >
        <Flex gap={32} vertical>
          <Form.List name="payouts">
            {(fields, { remove }) => (
              <>
                {fields.map((f) => (
                  <ManualPayoutsFormItem
                    available={p[f.key]}
                    currencies={currencies}
                    errors={formErrors}
                    field={f}
                    onRemove={remove}
                  />
                ))}
              </>
            )}
          </Form.List>

          <Flex align="center" justify="space-between">
            <ManualPayoutsFormFee />

            <Flex align="center" gap={16}>
              <Button
                icon={<PlusOutlined />}
                id="GENERATED_ID_MASSPAYOUTS_BUTTON_ADD_NEW_RECIPIENT_1"
                onClick={formAdd}
                size="large"
                type="primary"
              >
                Add new recipient
              </Button>

              <SubmitButton
                buttonProps={{
                  loading: collection.isLoading,
                  size: "large",
                  type: "primary",
                }}
                form={form}
              >
                Pre-Check
              </SubmitButton>
            </Flex>
          </Flex>
        </Flex>
      </Form>
    )
  },
)
